import React, { useRef, useEffect, useState } from "react";
import {
  View,
  Modal,
  TouchableWithoutFeedback,
  Dimensions,
} from "react-native";

interface DropdownMenuProps {
  visible: boolean;
  handleClose: () => void;
  handleOpen: () => void;
  trigger: React.ReactNode;
  children: React.ReactNode;
  dropdownWidth?: number;
  alignment?: "left" | "center" | "right";
  className?: string;
  containerClassName?: string;
  dropdownPosition?: "above" | "below";
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  visible,
  handleOpen,
  handleClose,
  trigger,
  children,
  dropdownWidth = 160,
  alignment = "center",
  className,
  containerClassName,
  dropdownPosition = "below",
}) => {
  const triggerRef = useRef<View>(null);
  const dropdownRef = useRef<View>(null);
  const [position, setPosition] = useState({ x: 0, y: 0, width: 0 });
  const [dropdownHeight, setDropdownHeight] = useState(0);
  const screenWidth = Dimensions.get("window").width;

  useEffect(() => {
    if (triggerRef.current && visible) {
      triggerRef.current.measure((fx, fy, width, height, px, py) => {
        let xPosition = px;

        switch (alignment) {
          case "left":
            xPosition = px;
            break;
          case "center":
            xPosition = px + width / 2 - dropdownWidth / 2;
            break;
          case "right":
            xPosition = px + width - dropdownWidth;
            break;
        }

        // Ensure dropdown stays within screen bounds
        xPosition = Math.max(
          0,
          Math.min(xPosition, screenWidth - dropdownWidth)
        );

        setPosition({
          x: xPosition,
          y: dropdownPosition === "above" ? py - dropdownHeight : py + height,
          width: width,
        });
      });
    }
  }, [
    visible,
    dropdownPosition,
    dropdownHeight,
    alignment,
    dropdownWidth,
    screenWidth,
  ]);

  const handleDropdownLayout = () => {
    if (dropdownRef.current) {
      dropdownRef.current.measure((fx, fy, width, height, px, py) => {
        setDropdownHeight(height);
      });
    }
  };

  return (
    <View className={`${containerClassName}`}>
      <TouchableWithoutFeedback onPress={handleOpen}>
        <View ref={triggerRef}>{trigger}</View>
      </TouchableWithoutFeedback>
      {visible && (
        <Modal
          transparent={true}
          visible={visible}
          animationType="fade"
          onRequestClose={handleClose}
        >
          <TouchableWithoutFeedback onPress={handleClose}>
            <View className="flex-1 justify-start items-start bg-transparent">
              <View
                ref={dropdownRef}
                onLayout={handleDropdownLayout}
                className={`absolute bg-white rounded-md p-2 shadow-lg space-y-2 ${className}`}
                style={{
                  top: position.y,
                  left: position.x,
                  width: dropdownWidth,
                }}
              >
                {children}
              </View>
            </View>
          </TouchableWithoutFeedback>
        </Modal>
      )}
    </View>
  );
};
export default DropdownMenu;
